/**
 * US-24-GR-42 — turns a rejected redriveDlqMessage call into operator-facing toast text.
 *
 * redriveDlqMessage throws `Error("HTTP {status}: {body text}")` on any non-OK response, so the
 * status and the API's own message are recovered by parsing that string.
 * Work item 19781: the shared 503 body is `{ error, code: "PROFILE_LOOKUP_UNAVAILABLE" }`, shown as the retry-safe fallback.
 */

const HTTP_ERROR = /^HTTP (\d{3}): ?([\s\S]*)$/;
const MESSAGE_FIELDS = ['message', 'errorMessage', 'error', 'detail', 'title'] as const;

const SERVICE_UNAVAILABLE_FALLBACK =
  'Redrive not performed: the service is unavailable. Nothing moved, so it is safe to retry.';

const PROFILE_LOOKUP_UNAVAILABLE = 'PROFILE_LOOKUP_UNAVAILABLE';

function hasCode(body: string, code: string): boolean {
  try {
    const parsed: unknown = JSON.parse(body.trim());
    return !!parsed && typeof parsed === 'object' && (parsed as Record<string, unknown>).code === code;
  } catch {
    return false;
  }
}

function extractServerMessage(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      for (const field of MESSAGE_FIELDS) {
        const value = record[field];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    }
  } catch {
    // Not JSON: fall through to the raw text.
  }
  return trimmed;
}

export function describeRedriveFailure(err: unknown): string {
  if (!(err instanceof Error)) return 'Redrive failed.';

  const match = HTTP_ERROR.exec(err.message);
  if (!match) return `Redrive failed: ${err.message}`;

  const status = match[1];
  if (status === '503' && hasCode(match[2], PROFILE_LOOKUP_UNAVAILABLE)) return SERVICE_UNAVAILABLE_FALLBACK;
  const serverMessage = extractServerMessage(match[2]);

  if (status === '503') return serverMessage || SERVICE_UNAVAILABLE_FALLBACK;
  return serverMessage
    ? `Redrive failed (HTTP ${status}): ${serverMessage}`
    : `Redrive failed (HTTP ${status})`;
}
